import { useState, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { ProtocolRevenueSection } from '@/components/dashboard/sections/ProtocolRevenueSection'
import { PlatformActivitySection } from '@/components/dashboard/sections/PlatformActivitySection'
import { UsersCommunitySection } from '@/components/dashboard/sections/UsersCommunitySection'
import { StakingHealthSection } from '@/components/dashboard/sections/StakingHealthSection'
import { TimePeriodToggle } from '@/components/dashboard/TimePeriodToggle'
import type { TimePeriod } from '@/types/dashboard'

const SESSION_KEY = 'dashboard-timeframe'
const VALID_PERIODS: TimePeriod[] = ['24h', '7d', '30d', 'all']

function getInitialPeriod(): TimePeriod {
  const stored = sessionStorage.getItem(SESSION_KEY)
  if (stored && VALID_PERIODS.includes(stored as TimePeriod)) {
    return stored as TimePeriod
  }
  return '7d'
}

export function DashboardPage() {
  const { adminUser } = useAuth()
  const [period, setPeriodState] = useState<TimePeriod>(getInitialPeriod)

  const setPeriod = useCallback((p: TimePeriod) => {
    setPeriodState(p)
    sessionStorage.setItem(SESSION_KEY, p)
  }, [])

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-500/10 text-red-500 border-red-500/20'
      case 'admin':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20'
      case 'viewer':
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20'
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20'
    }
  }

  const formatRole = (role: string) => {
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">
            Welcome back{adminUser?.email ? `, ${adminUser.email.split('@')[0]}` : ''}
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            Live data from dextoro platform.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <TimePeriodToggle value={period} onChange={setPeriod} />
          {adminUser?.role && (
            <div className={`px-3 py-1 rounded-full border text-xs sm:text-sm font-medium w-fit ${getRoleBadgeColor(adminUser.role)}`}>
              {formatRole(adminUser.role)}
            </div>
          )}
        </div>
      </div>

      {/* Section 1: Protocol Revenue */}
      <ProtocolRevenueSection period={period} />

      {/* Section 2: Platform Activity */}
      <PlatformActivitySection period={period} />

      {/* Section 3: Users & Community */}
      <UsersCommunitySection period={period} />

      {/* Section 4: DTR Staking Health */}
      <StakingHealthSection />
    </div>
  )
}
